import {
  PLAN_IDS,
  getPlanById,
  getContributionDiscountSummary,
  computeEffectivePriceCents,
  formatCentsAsDollars,
} from '../constants/subscriptionPlans';
import {
  getPrimaryTourPartyCoverage,
  hasTourPartyCrewAccess,
} from './tourPartySubscription';

/** Active, or cancelled but still within paid period. */
export function isSubscriptionActive(user) {
  if (!user) return false;
  if (user.subscriptionStatus === 'active') return true;
  if (
    user.subscriptionStatus === 'cancelled' &&
    user.subscriptionCancelAt &&
    new Date(user.subscriptionCancelAt) > new Date()
  ) {
    return true;
  }
  return false;
}

export function hasPaidProductAccess(user) {
  return isSubscriptionActive(user) || hasTourPartyCrewAccess(user);
}

export function getUserSubscriptionPlanId(user) {
  if (!user) return null;
  if (isSubscriptionActive(user)) {
    // Map-only and yearly keep their own plan; every other active subscriber gets
    // everything, whatever (legacy or unknown) plan id is on file.
    const planId = user.subscriptionPlan;
    if (planId === PLAN_IDS.MAP_ONLY || planId === PLAN_IDS.VRC_MEMBER_YEARLY) return planId;
    return PLAN_IDS.VRC_MEMBER;
  }
  if (hasTourPartyCrewAccess(user)) return PLAN_IDS.VRC_MEMBER;
  return null;
}

export function hasCalendarAccess(user) {
  if (hasTourPartyCrewAccess(user)) return true;
  const plan = getPlanById(getUserSubscriptionPlanId(user));
  return !!(plan && plan.includesCalendar);
}

export function hasMapAccess(user) {
  if (hasTourPartyCrewAccess(user)) return true;
  const plan = getPlanById(getUserSubscriptionPlanId(user));
  return !!(plan && plan.includesMap);
}

/**
 * Hard entitlement gate for paid features:
 * - Calendar / Map require an active matching plan or tour-party crew coverage
 * - Unsubscribed users are blocked (nav + screens should enforce this)
 * Admins / DEV_MODE bypasses live in FeatureFlagsContext, not here.
 */
export function passesSubscriptionSoftGate(user, { requireCalendar = false, requireMap = false } = {}) {
  if (requireMap) return hasMapAccess(user);
  if (requireCalendar) return hasCalendarAccess(user);
  return hasPaidProductAccess(user);
}

/** Alias for clarity at call sites. */
export function passesSubscriptionGate(user, opts) {
  return passesSubscriptionSoftGate(user, opts);
}

export function getSubscriptionDisplayInfo(user) {
  const planId = getUserSubscriptionPlanId(user);
  const plan = getPlanById(planId);
  const approved =
    user?.subscriptionApprovedLocationsThisCycle ??
    0;
  const summary = plan?.allowsContributionDiscount
    ? getContributionDiscountSummary(approved)
    : null;

  const storedEffective = user?.subscriptionEffectivePriceCentsThisCycle;
  const effectiveCents =
    storedEffective != null
      ? storedEffective
      : computeEffectivePriceCents(planId, approved);

  const crewCoverage = getPrimaryTourPartyCoverage(user);

  return {
    active: hasPaidProductAccess(user),
    selfSubscribed: isSubscriptionActive(user),
    tourPartyCrewCoverage: crewCoverage,
    planId,
    plan,
    planName: plan?.name || (hasPaidProductAccess(user) ? 'Calendar + Map' : null),
    status: user?.subscriptionStatus || null,
    cancelAt: user?.subscriptionCancelAt || null,
    contributionSummary: summary,
    effectivePriceLabel: effectiveCents != null ? formatCentsAsDollars(effectiveCents) : null,
    cycleStart: user?.subscriptionBillingCycleStart || null,
    cycleEnd: user?.subscriptionBillingCycleEnd || null,
  };
}

export {
  PLAN_IDS,
  getPlanById,
  getContributionDiscountSummary,
  formatCentsAsDollars,
  hasTourPartyCrewAccess,
};
